import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtQty, parseNum } from "@/lib/format";
import { Field, Loading, btnGhost, btnPrimary, inputCls } from "@/components/app/common";

export function RimuoviDialog({ prodotto, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setQ(""); setNote(""); }, [prodotto]);
  const go = async (azzera) => {
    const quantita = parseNum(q);
    if (!azzera && !(quantita > 0)) return toast.error("Indica quanti pezzi rimuovere");
    setBusy(true);
    try {
      const { data } = await api.post(`/magazzino/${prodotto.id}/rimuovi`, { quantita: azzera ? null : quantita, azzera, note });
      toast.success(`Giacenza aggiornata: ${fmtQty(data.prodotto.giacenza)} pz`);
      onDone(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!prodotto} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="rimuovi-dialog">
        <DialogHeader>
          <DialogTitle>Rimuovi quantità</DialogTitle>
          <DialogDescription><b>{prodotto?.nome}</b> · giacenza attuale {fmtQty(prodotto?.giacenza)} pz. Viene registrata una rettifica «Correzione errore caricamento», tracciata nei movimenti.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Pezzi da rimuovere"><Input data-testid="rimuovi-quantita-input" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls} placeholder="0" /></Field>
          <Field label="Nota"><Input data-testid="rimuovi-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Es. caricato due volte" /></Field>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => go(true)} disabled={busy} data-testid="rimuovi-azzera">Azzera giacenza</Button>
          <Button className={btnPrimary} onClick={() => go(false)} disabled={busy} data-testid="rimuovi-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rimuovi"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const LAB = { acquisti: "acquisti (carichi da fattura)", vendite: "vendite mensili", uscite: "uscite merce", incassi_uscite: "incassi collegati alle uscite", rettifiche: "rettifiche inventario", mappature: "abbinamenti salvati" };

export function EliminaReferenzaDialog({ prodotto, onClose, onDone }) {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPrev(null);
    if (prodotto) api.get(`/prodotti/${prodotto.id}/elimina-referenza`).then((r) => setPrev(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [prodotto]);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/prodotti/${prodotto.id}/elimina-referenza`);
      toast.success(`«${prodotto.nome}» eliminato${prev.fatture.length ? ` · ${prev.fatture.length} fatture tornate in bozza` : ""}`);
      onDone();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!prodotto} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="elimina-referenza-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#A2513C]"><Trash2 className="h-5 w-5" />Elimina referenza</DialogTitle>
          <DialogDescription>Elimina definitivamente <b>{prodotto?.nome}</b> con tutto ciò che è collegato.</DialogDescription>
        </DialogHeader>
        {!prev ? <Loading /> : (
          <div className="space-y-3 text-sm" data-testid="elimina-referenza-riepilogo">
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {Object.entries(prev.conteggi).map(([k, v]) => <li key={k} data-testid={`elimina-count-${k}`}><b className="tabular">{v}</b> {LAB[k]}</li>)}
              {prev.prodotto.giacenza_iniziale > 0 && <li><b>{fmtQty(prev.prodotto.giacenza_iniziale)}</b> pz di giacenza iniziale</li>}
            </ul>
            {prev.fatture.length > 0 ? (
              <div className="rounded-xl bg-[#E07A5F]/10 p-3 text-xs" data-testid="elimina-referenza-fatture">
                <div className="font-semibold text-[#A2513C] mb-1">{prev.righe_fattura} righe di fattura diventeranno «da abbinare» e queste fatture torneranno in BOZZA (i loro costi vengono rimossi finché non le riconfermi):</div>
                <ul className="space-y-0.5">{prev.fatture.map((f) => <li key={f.id}>{f.numero || "s.n."} · {f.fornitore_nome} · {fmtDate(f.data)} ({f.stato})</li>)}</ul>
              </div>
            ) : <div className="text-xs text-[#756B64]">Nessuna fattura collegata.</div>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className={btnGhost} onClick={onClose} disabled={busy} data-testid="elimina-referenza-annulla">Annulla</Button>
          <Button className="rounded-full bg-[#B4553D] hover:bg-[#A2513C] text-white" onClick={go} disabled={!prev || busy} data-testid="elimina-referenza-conferma">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina definitivamente"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
